import { createSlice } from "@reduxjs/toolkit";
import {
  getSettingWithFilter,
  updateSetting,
  deleteSetting,
  bulkDeleteSetting,
  getSettingWithIDS,
  createSetting,
} from "./thunk";

const initialState = {
  settings: {
    data: [],
    count: 0,
  },
  singleSetting: null,
  settingInfo: null,
  loading: false,
  error: null,
  filterParams: {
    currentPage: 1,
    perPage: 20,
  },
};

const settingSlice = createSlice({
  name: "setting",
  initialState,
  reducers: {
    setFilterParams: (state, action) => {
      state.filterParams = action.payload;
    },
    clearSingleSetting: (state) => {
      state.singleSetting = null;
    },
    clearSettingInfo: (state) => {
      state.settingInfo = null;
    },
  },
  extraReducers: (builder) => {
    builder

      // 🔄 FETCH SETTINGS
      .addCase(getSettingWithFilter.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSettingWithFilter.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = {
          data: action.payload?.data || [],
          count: action.payload?.count || 0,
        };
        state.error = null;
      })
      .addCase(getSettingWithFilter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch settings";
      })

      // 🔍 FETCH SINGLE SETTING
      .addCase(getSettingWithIDS.pending, (state) => {
        state.loading = true;
        state.singleSetting = null;
      })
      .addCase(getSettingWithIDS.fulfilled, (state, action) => {
        state.loading = false;
        state.singleSetting = action.payload;
        state.error = null;
      })
      .addCase(getSettingWithIDS.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch setting by ID";
      })

      // ➕ CREATE SETTING
      .addCase(createSetting.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSetting.fulfilled, (state, action) => {
        state.loading = false;
        const newSetting = action.payload;
        if (Array.isArray(state.settings.data)) {
          state.settings.data = [...state.settings.data, newSetting];
        } else {
          state.settings.data = [newSetting];
        }
        state.error = null;
      })
      .addCase(createSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create setting";
      })

      // ✏️ UPDATE SETTING
      .addCase(updateSetting.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload?.data;
        if (updated && Array.isArray(state.settings.data)) {
          const index = state.settings.data.findIndex((s) => s.id === updated.id);
          if (index !== -1) {
            state.settings.data[index] = updated;
          }
        }
      })

      // ❌ DELETE SETTING
      .addCase(deleteSetting.fulfilled, (state, action) => {
        const deletedId = action.payload;
        if (deletedId && Array.isArray(state.settings.data)) {
          state.settings.data = state.settings.data.filter(s => s.id !== deletedId);
        }
      })

      // 🗑 BULK DELETE SETTINGS
      .addCase(bulkDeleteSetting.fulfilled, (state, action) => {
        const deletedIds = action.payload;
        if (Array.isArray(state.settings.data)) {
          state.settings.data = state.settings.data.filter(
            (setting) => !deletedIds.includes(setting.id)
          );
        }
      });
  },
});

export const { setFilterParams, clearSingleSetting, clearSettingInfo } = settingSlice.actions;
export default settingSlice.reducer;
